import {
	Component,
	Input,
	Output,
	EventEmitter,
	forwardRef,
	OnDestroy,
	ElementRef,
	ViewChild,
	ChangeDetectorRef,
	ChangeDetectionStrategy,
	HostListener,
	AfterViewInit,
	Self,
	Optional
} from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { IdService } from "../shared/services/id.service";

import { Observable } from "rxjs";

export interface CheckboxUpdateEvent {
	id: string;
	checked: boolean;
}

/**
 * Defines the set of states for a checkbox component.
 */
 export enum CheckboxState {
	Init,
	Indeterminate,
	Checked,
	Unchecked
}

export class CheckboxUpdate {
	id: string;
	checked: boolean;
}

@Component({
	selector: "rbc-checkbox",
	template: `
		<div
		[ngClass]="classGen()"
		class="rbc-checkbox"
		(mouseout)="removeFocus()"
		#checkboxWrap>
			<input
				#checkbox
				[id]="id"
				[checked]="checked"
				[disabled]="disabled"
				[attr.name]= "name ? name: null"
				class= "rbc-checkbox-control"
				type= "checkbox"
				[attr.aria-label]= "ariaGen()"
				[attr.aria-checked]="(indeterminate ? 'mixed' : checked ? 'true' : 'false')"
				(change)="onChange($event)"
				(click)="onClick($event)">
				<label
					class="rbc-checkbox-label"
					[id]= "id + 'Label'"
					[for]= "id"
					[ngClass]="{ disabled: disabled, 'rbc-checkbox-indeterminate': indeterminate }"
				>
				<rbc-icon
					[attr.aria-hidden]="true"
					[icon]= "indeterminate ? 'minus-square' : checked ? 'check-square' : 'square'"
					class="rbc-checkbox-icon"
				></rbc-icon>
					<ng-container *ngIf="!labelSrOnly">{{label}}</ng-container>
					<ng-content></ng-content>
				</label>
		</div>
	`,
	styles: [],
	changeDetection: ChangeDetectionStrategy.OnPush

})
export class CheckboxComponent implements ControlValueAccessor, OnDestroy {
	@Input() id: string;
	@Input() disabled: boolean;
	@Input() name: string;
	@Input() labelSrOnly: boolean;
	@Input() label: string;
	@Input() wrapperClass;
	@Input() ariaLabel;

	@ViewChild("checkbox", { static: false }) checkboxRef: ElementRef;
	@ViewChild("checkboxWrap", { static: false }) checkboxWrap: ElementRef;

	mouseoutObservable: Observable<any>;

	/**
	 * Set the checkbox's indeterminate state to match the parameter and transition the view to reflect the change.
	 *
	 * Allows double binding with the `indeterminateChange` Output.
	 */
	 @Input() set indeterminate(indeterminate: boolean) {
		if (indeterminate === this._indeterminate) {
			return;
		}

		this._indeterminate = indeterminate;

		if (this._indeterminate) {
			this.transitionCheckboxState(CheckboxState.Indeterminate);
		} else {
			this.transitionCheckboxState(this.checked ? CheckboxState.Checked : CheckboxState.Unchecked);
		}
		if(this.checkboxRef){
			this.checkboxRef.nativeElement.indeterminate = indeterminate;
			this.changeDetectorRef.markForCheck();
			this.indeterminateChange.emit(this._indeterminate);
		}
	}

	/**
	 * Reflects whether the checkbox state is indeterminate.
	 */
	get indeterminate() {
		return this._indeterminate;
	}

	/**
	 * Sets the `checked` state. `true` for checked, `false` for unchecked
	 *
	 * Allows double binding with the `checkBoxUpdate` Output.
	 */
	@Input() set checked (checked: boolean) {
		this.setChecked(checked, false);
	}

	/**
	 * Returns value `true` if state is selected for the checkbox.
	 */
	get checked() {
		return this._checked;
	}

	/**
	 * Emits click event.
	 * Event emitter that allows you to control the model manually
	 * @deprecated use `checkBoxUpdate` instead
	 */
	@Output() click = new EventEmitter<any>();

	/**
	 * Emits click event.
	 * Event emitter that allows you to control the model manually
	 */
	@Output() onClickEmitter = new EventEmitter<any>();

	/**
	 * Emits click event without giving parent component control..
	 */
	@Output() checkBoxClick = new EventEmitter<any>();

	/**
	 * Emits event notifying other classes when a change in state occurs on a checkbox after a
	 * click.
	 *
	 * @deprecated use `checkBoxUpdate` instead
	 */
	@Output() change = new EventEmitter<any>();

	/**
	 * Emits an event when the value of the checkbox changes.
	 *
	 * Allows double biding with the `checked` Input.
	 */
	 @Output() checkBoxUpdate = new EventEmitter<boolean>(); // id and checked state object

	/**
	 * Emits event notifying other classes when a change in state occurs specifically
	 * on an indeterminate checkbox.
	 */
	@Output() indeterminateChange = new EventEmitter<boolean>();

	/**
	 * Set to `true` if the input checkbox is selected (or checked).
	 */
	_checked = false;
	/**
	 * Set to `true` if the input checkbox is in state indeterminate.
	 */
	_indeterminate = false;

	/**
	 * Keeps a reference to the checkboxes current state, as defined in `CheckboxState`.
	 */
	currentCheckboxState = CheckboxState.Init;

	constructor(private idService: IdService, protected changeDetectorRef: ChangeDetectorRef, @Optional() @Self() private controlDirective: NgControl) {
		this.id = this.id ? this.id : this.idService.generate();
		if(this.controlDirective){
			controlDirective.valueAccessor = this;
		}
	}

	ngOnDestroy() {
		this.idService.remove(this.id);
	}

	/**
	 * Toggle the selected state of the checkbox.
	 */
	public toggle() {
		// Flip checked and reset indeterminate
		this.setChecked(!this.checked, true);
	}

	/**
	 * Writes a value from `ngModel` to the component.
	 *
	 * In this case the value is the `checked` property.
	 *
	 * @param value boolean, corresponds to the `checked` property.
	 */
	public writeValue(value: any) {
		this.setChecked(!!value, true);
	}

	/**
	 * Sets a method in order to propagate changes back to the form.
	 */
	public registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	/**
	 * Registers a callback to be triggered when the control has been touched.
	 * @param fn Callback to be triggered when the checkbox is touched.
	 */
	public registerOnTouched(fn: any) {
		this.onTouched = fn;
	}

	/**
	 * `ControlValueAccessor` method to programmatically disable the checkbox.
	 *
	 * ex: `this.formGroup.get("myCheckbox").disable();`
	 *
	 * @param isDisabled `true` to disable the checkbox
	 */
	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.checkboxRef.nativeElement.disabled = this.disabled;
	}

	@HostListener("focusout")
	focusOut() {
		this.onTouched();
	}

	/**
	 * Executes on the event of a change within `Checkbox` to block propagation.
	 */
	onChange(event: Event) {
		event.stopPropagation();
	}

	/**
	 * Handles click events on the `Checkbox` and emits changes to other classes.
	 */
	onClick(event: Event) {
		if (this.onClickEmitter.observers.length) {
			// Disable default checkbox activation behavior which flips checked and resets indeterminate.
			// This allows the parent component to control the checked/indeterminate properties.
			event.preventDefault();
			this.onClickEmitter.emit(event);
			return;
		} else {
			this.checkBoxClick.emit(event);

			if (!this.disabled) {
				this.toggle();
				this.transitionCheckboxState(this._checked ? CheckboxState.Checked : CheckboxState.Unchecked);
				this.emitChangeEvent();
			}
		}
	}


	/**
	 * Called when checkbox is blurred. Needed to properly implement `ControlValueAccessor`.
	 */
	onTouched: () => any = () => {};

	/**
	 * Handles changes between checkbox states.
	 */
	transitionCheckboxState(newState: CheckboxState) {
		this.currentCheckboxState = newState;
	}

	/**
	 * Creates instance of `CheckboxChange` used to propagate the change event.
	 */
	emitChangeEvent() {
		this.change.emit(event);
		//end deprecation

		this.checkBoxUpdate.emit(this.checked);
		this.propagateChange(this.checked);
	}



	/**
	 * Method set in `registerOnChange` to propagate changes back to the form.
	 */
	propagateChange = (_: any) => {};

	 /**
	  * Sets checked state and optionally resets indeterminate state.
	  */
	private setChecked(checked: boolean, resetIndeterminate: boolean) {
		if (checked === this._checked) {
			return;
		}
		this._checked = checked;
		// Reset indeterminate if requested
		if (resetIndeterminate && this._indeterminate) {
			this._indeterminate = false;
			Promise.resolve().then(() => {
				this.indeterminateChange.emit(this._indeterminate);
			});
		}
		this.changeDetectorRef.markForCheck();
	}


	removeFocus() {
		this.checkboxRef.nativeElement.blur();
		this.mouseoutObservable = null;
	}

	classGen() {
		let classes = {};
		classes[this.wrapperClass] = this.wrapperClass;

		return classes;
	}

	ariaGen() {
		if (this.labelSrOnly) {
			return this.label;
		} else if (this.ariaLabel) {
			return this.ariaLabel;
		} else {
			return null;
		}
	}
}
